import { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import Modal from '../../../shared/components/Modal';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { CURRENT_SCHOOL_YEAR, getSchoolYearOptions } from '../../../shared/constant/schoolYears';
import { deriveTrackFromStrand, getStrandsForGradeLevel } from '../../../shared/constant/strands';

// `allowedGroups`: for teachers, the [{ grade_level, section }] pairs they teach or advise, so a
// student they add lands in one of their own sections. Leave it out to allow any.
function StudentFormModal({ student, allowedGroups, onClose, onSubmit }) {
  const [lrn, setLrn] = useState(student?.lrn ?? '');
  const [firstname, setFirstname] = useState(student?.firstname ?? '');
  const [lastname, setLastname] = useState(student?.lastname ?? '');
  const [middlename, setMiddlename] = useState(student?.middlename ?? '');
  const [age, setAge] = useState(student?.age != null ? String(student.age) : '');
  const [gender, setGender] = useState(student?.gender ?? '');
  const [gradeLevel, setGradeLevel] = useState(
    student?.grade_level ?? allowedGroups?.[0]?.grade_level ?? GRADE_LEVELS[0].value
  );
  const [section, setSection] = useState(student?.section ?? allowedGroups?.[0]?.section ?? '');
  const gradeLevelOptions = allowedGroups
    ? GRADE_LEVELS.filter((g) => allowedGroups.some((group) => group.grade_level === g.value))
    : GRADE_LEVELS;
  const sectionOptions = allowedGroups
    ? [...new Set(allowedGroups.filter((g) => g.grade_level === gradeLevel).map((g) => g.section))].sort()
    : null;
  const [schoolYear, setSchoolYear] = useState(student?.school_year ?? CURRENT_SCHOOL_YEAR);
  const [strand, setStrand] = useState(student?.strand ?? '');
  const [username, setUsername] = useState(student?.username ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const strandOptions = getStrandsForGradeLevel(gradeLevel);
  const showStrand = strandOptions.length > 0;
  const track = showStrand ? deriveTrackFromStrand(strand) : null;

  const handleGradeLevelChange = (value) => {
    setGradeLevel(value);
    if (allowedGroups) {
      setSection(allowedGroups.find((g) => g.grade_level === value)?.section ?? '');
    }
    if (!getStrandsForGradeLevel(value).some((s) => s.code === strand)) {
      setStrand('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const digits = lrn.trim();
    if (!/^\d{12}$/.test(digits)) {
      setError('LRN must be exactly 12 digits.');
      return;
    }

    // A blank username falls back to the LRN.
    const login = username.trim() || digits;
    if (!/^[A-Za-z0-9._-]{3,50}$/.test(login)) {
      setError('Usernames are 3-50 letters, numbers, dots, dashes or underscores.');
      return;
    }

    if (password && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        lrn: digits,
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        middlename: middlename.trim() || null,
        age: age === '' ? null : Number(age),
        gender: gender || null,
        grade_level: gradeLevel,
        section: section.trim() || null,
        school_year: schoolYear || null,
        strand: showStrand ? strand || null : null,
        track: showStrand ? track : null,
        username: login,
        password: password || undefined,
      });
    } catch (err) {
      setError(err.message || 'Failed to save student');
      setSaving(false);
    }
  };

  return (
    <Modal title={student ? 'Edit Student' : 'Add Student'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <div className="space-y-1">
          <label htmlFor="lrn" className="text-sm font-medium text-gray-700">LRN</label>
          <input
            id="lrn"
            type="text"
            inputMode="numeric"
            maxLength={12}
            required
            value={lrn}
            onChange={(e) => setLrn(e.target.value.replace(/\D/g, ''))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="firstname" className="text-sm font-medium text-gray-700">First Name</label>
            <input
              id="firstname"
              type="text"
              required
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="lastname" className="text-sm font-medium text-gray-700">Last Name</label>
            <input
              id="lastname"
              type="text"
              required
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="middlename" className="text-sm font-medium text-gray-700">Middle Name</label>
          <input
            id="middlename"
            type="text"
            value={middlename}
            onChange={(e) => setMiddlename(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="age" className="text-sm font-medium text-gray-700">Age</label>
            <input
              id="age"
              type="number"
              inputMode="numeric"
              min="1"
              max="99"
              step="1"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="gender" className="text-sm font-medium text-gray-700">Gender</label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            >
              <option value="">Select gender</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="gradeLevel" className="text-sm font-medium text-gray-700">Grade Level</label>
            <select
              id="gradeLevel"
              value={gradeLevel}
              onChange={(e) => handleGradeLevelChange(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            >
              {gradeLevelOptions.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="section" className="text-sm font-medium text-gray-700">Section</label>
            {sectionOptions ? (
              <select
                id="section"
                required
                value={section}
                onChange={(e) => setSection(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              >
                <option value="">Select section</option>
                {sectionOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            ) : (
              <input
                id="section"
                type="text"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              />
            )}
          </div>
        </div>

        {showStrand && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label htmlFor="strand" className="text-sm font-medium text-gray-700">Strand</label>
              <select
                id="strand"
                value={strand}
                onChange={(e) => setStrand(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              >
                <option value="">Select strand</option>
                {strandOptions.map((s) => (
                  <option key={s.code} value={s.code}>{s.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <span className="text-sm font-medium text-gray-700">Track</span>
              <p className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                {track || '—'}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-1">
          <label htmlFor="schoolYear" className="text-sm font-medium text-gray-700">School Year</label>
          <select
            id="schoolYear"
            value={schoolYear}
            onChange={(e) => setSchoolYear(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          >
            {getSchoolYearOptions().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="username" className="text-sm font-medium text-gray-700">Username</label>
            <input
              id="username"
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              placeholder={lrn || 'Defaults to LRN'}
              value={username}
              onChange={(e) => setUsername(e.target.value.trim())}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="text-sm font-medium text-gray-700">
              {student?.has_login ? 'New Password' : 'Password'}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 -mt-1">
          {student?.has_login
            ? 'Leave the password blank to keep the current one. Setting a new one signs the student out.'
            : "The student signs in with this username and password. Leave the password blank if they shouldn't sign in yet."}
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-[#0b4a71] px-4 py-2 text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default StudentFormModal;
